import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { AgentPresenceMenu } from './components/agent-presence-menu.tsx'
import { AlertBanner } from './components/alert-banner.tsx'
import { AvatarMenu } from './components/avatar-menu.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Logo } from './components/logo.tsx'
import {
	isEngagementFiche,
	isTabActive,
	MobileTabBar,
} from './components/mobile-tab-bar.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SoftphoneProvider } from './components/softphone/softphone-provider.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getOwnAgentPresence } from './utils/agent-presence.server.ts'
import { getDismissedAlerts } from './utils/alert-dismiss.server.ts'
import { isAppSettingEnabled } from './utils/app-settings.server.ts'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { cn, combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getUserEffectivePermissions } from './utils/permissions.service.ts'
import {
	getVisibleTabsForUser,
	type TabKey,
} from './utils/tab-visibility.server.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useScrollDirection } from './utils/use-scroll-direction.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Webapp PLQ' : 'Erreur | Webapp PLQ' },
		{
			name: 'description',
			content: 'Plateforme de campagne du Parti libéral du Québec',
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				async () => {
					const userData = await prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							// Call-center agent link — drives the header presence icon.
							supportAgentId: true,
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					})

					if (!userData) return null

					// Get effective permissions (includes hierarchy + direct permissions)
					const effectivePermissions = await getUserEffectivePermissions(
						userData.id,
					)

					return {
						...userData,
						effectivePermissions: effectivePermissions.map((p) => ({
							entity: p.entity,
							action: p.action,
							access: p.access,
						})),
					}
				},
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	const now = new Date()
	const activeAlertsRaw = userId
		? await prisma.alert.findMany({
				where: {
					isPublished: true,
					startsAt: { lte: now },
					endsAt: { gte: now },
				},
				select: { id: true, message: true, url: true, dismissible: true },
				orderBy: { startsAt: 'desc' },
			})
		: []
	const dismissed = getDismissedAlerts(request)
	const activeAlerts = activeAlertsRaw.filter((a) => !dismissed.includes(a.id))

	const [visibleTabsSet, relatedContentEnabled, agentPresence] =
		await Promise.all([
			getVisibleTabsForUser(userId),
			isAppSettingEnabled('related-content'),
			// Only for the few members linked to a call-center agent; memoised
			// in-process for 15 s so it doesn't hit Support-center on every
			// navigation. See agent-presence.server.ts.
			user?.supportAgentId ? getOwnAgentPresence(user.supportAgentId) : null,
		])
	const visibleTabs = Array.from(visibleTabsSet)

	return data(
		{
			user,
			agentPresence,
			activeAlerts,
			relatedContentEnabled,
			visibleTabs,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-clip`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,viewport-fit=cover"
				/>
				<meta name="apple-mobile-web-app-capable" content="yes" />
				<meta name="mobile-web-app-capable" content="yes" />
				<meta name="apple-mobile-web-app-status-bar-style" content="default" />
				<meta name="apple-mobile-web-app-title" content="PLQ" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

const desktopNavLinks = [
	{ path: '/', label: 'Accueil' },
	{ path: '/lignes', label: 'Lignes', tabKey: 'lignes' },
	{ path: '/cartes', label: 'Jeu de cartes', tabKey: 'cartes' },
	{ path: '/engagements', label: 'Engagements', tabKey: 'plateforme' },
	{ path: '/evenements', label: 'Événements', tabKey: 'evenements' },
	{ path: '/support', label: 'Soutien' },
] as const satisfies ReadonlyArray<{
	path: string
	label: string
	tabKey?: TabKey
}>

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const { pathname } = useLocation()
	const scrollDirection = useScrollDirection()
	const hidden = scrollDirection === 'down'
	const isImmersive = pathname.startsWith('/cartes/terrain')
	// The engagements fiche is immersive on phones only — its own top bar stands
	// in for the app chrome there. From `md` up it stays a normal page: site
	// header, tab bar, and the bottom padding that keeps the bar off the content.
	const isPhoneImmersive = isEngagementFiche(pathname)
	useToast(data.toast)

	const visibleTabs = new Set<TabKey>(data.visibleTabs)
	const filteredDesktopNavLinks = desktopNavLinks.filter(
		(link) => !('tabKey' in link) || visibleTabs.has(link.tabKey),
	)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col justify-between">
				{!isImmersive ? (
					<div className={isPhoneImmersive ? 'max-md:hidden' : undefined}>
						<AlertBanner alerts={data.activeAlerts} />
					</div>
				) : null}
				<header
					hidden={isImmersive}
					className={cn(
						'border-border/60 bg-background/75 sticky top-0 isolate z-30 border-b backdrop-blur-xl backdrop-saturate-150 transition-transform duration-200',
						isPhoneImmersive && 'max-md:hidden',
						hidden ? '-translate-y-full xl:translate-y-0' : 'translate-y-0',
					)}
				>
					<div className="container py-4">
						<nav className="flex items-center justify-between gap-4 md:gap-8">
							<Logo />

							{user && (
								<ul className="hidden flex-1 items-center justify-center gap-1 xl:flex">
									{filteredDesktopNavLinks.map(({ path, label }) => {
										const active = isTabActive(pathname, path)
										return (
											<li key={path}>
												<Link
													to={path}
													prefetch="intent"
													aria-current={active ? 'page' : undefined}
													className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
														active
															? 'text-primary'
															: 'text-muted-foreground hover:bg-muted hover:text-foreground'
													}`}
												>
													{label}
												</Link>
											</li>
										)
									})}
								</ul>
							)}

							<div className="flex items-center gap-1 sm:gap-2">
								{user?.supportAgentId ? (
									<AgentPresenceMenu presence={data.agentPresence} />
								) : null}
								{user ? <AvatarMenu /> : null}
							</div>
						</nav>
					</div>
				</header>

				<div
					className={cn(
						'flex flex-1 flex-col',
						user &&
							!isImmersive &&
							'pb-[calc(3.5rem+env(safe-area-inset-bottom))] xl:pb-0',
						// The bar is gone below `md` on the fiche, so its clearance is too.
						isPhoneImmersive && 'max-md:pb-0',
					)}
				>
					<Outlet />
				</div>

				<MobileTabBar visibleTabs={data.visibleTabs} />
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			{/* Browser softphone for members linked to a call-center agent — lives
			    here so a call in progress survives navigation. */}
			<SoftphoneProvider available={Boolean(data.user?.supportAgentId)}>
				<App />
			</SoftphoneProvider>
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
